/* =============================================================
   CozyPedic Shopify Theme — Main JS
   Uses Shopify's Cart AJAX API for all cart operations.
   ============================================================= */

// ---------- Cart AJAX helpers ----------
async function cartFetch() {
  const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  return res.json();
}

async function cartAdd(variantId, quantity = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: [{ id: variantId, quantity }] })
  });
  return res.json();
}

async function cartChange(line, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ line, quantity })
  });
  return res.json();
}

function formatMoney(cents) {
  return '$' + (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ---------- Cart UI ----------
async function updateCartCount() {
  const cart = await cartFetch();
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = cart.item_count;
    el.style.display = cart.item_count > 0 ? '' : 'none';
  });
  return cart;
}

async function renderCart() {
  const cart = await cartFetch();
  const container = document.querySelector('[data-cart-items]');
  const footer = document.querySelector('[data-cart-footer]');
  if (!container) return;
  if (cart.item_count === 0) {
    container.innerHTML = `
      <div class="cart-empty">
        <p>Your cart is quiet, for now.</p>
        <a href="/collections/all" class="btn btn-outline">Explore the Collection</a>
      </div>`;
    if (footer) footer.style.display = 'none';
    return;
  }
  if (footer) footer.style.display = '';
  container.innerHTML = cart.items.map((item, i) => `
    <div class="cart-item">
      <div class="cart-item-img" style="background-image:url('${item.image || item.featured_image?.url || ''}');"></div>
      <div class="cart-item-info">
        <h4>${item.product_title}</h4>
        <span>${item.variant_title || ''}${item.quantity > 1 ? ` · Qty ${item.quantity}` : ''}</span>
        <a class="cart-item-remove" href="#" data-remove-line="${i + 1}">Remove</a>
      </div>
      <div class="cart-item-price">${formatMoney(item.line_price)}</div>
    </div>
  `).join('');
  container.querySelectorAll('[data-remove-line]').forEach(el => {
    el.addEventListener('click', async e => {
      e.preventDefault();
      const line = parseInt(el.getAttribute('data-remove-line'));
      await cartChange(line, 0);
      await renderCart();
      await updateCartCount();
    });
  });
  const subtotal = document.querySelector('[data-cart-subtotal]');
  if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
}

function openCart() {
  document.querySelector('.cart-drawer')?.classList.add('open');
  document.querySelector('.cart-overlay')?.classList.add('open');
  renderCart();
}
function closeCart() {
  document.querySelector('.cart-drawer')?.classList.remove('open');
  document.querySelector('.cart-overlay')?.classList.remove('open');
}

// ---------- Toast ----------
function showToast(msg) {
  const toast = document.querySelector('[data-toast]');
  if (!toast) return;
  toast.textContent = msg;
  toast.classList.add('show');
  clearTimeout(showToast._t);
  showToast._t = setTimeout(() => toast.classList.remove('show'), 2400);
}

// ---------- PDP: Size selector, gallery, add-to-cart ----------
function initPdp() {
  const pdp = document.querySelector('[data-pdp]');
  if (!pdp) return;

  const priceEl = pdp.querySelector('[data-pdp-price]');
  const sizeLabel = pdp.querySelector('[data-size-label]');
  const stickyName = document.querySelector('[data-sticky-name]');
  const stickyPrice = document.querySelector('[data-sticky-price]');
  const stickySize = document.querySelector('[data-sticky-size]');
  const productName = pdp.getAttribute('data-product-name') || '';

  let selectedVariantId = pdp.getAttribute('data-default-variant');

  function updateSelection(btn) {
    const variantId = btn.getAttribute('data-variant-id');
    const size = btn.getAttribute('data-size');
    const priceCents = parseInt(btn.getAttribute('data-price'));
    selectedVariantId = variantId;

    const priceStr = formatMoney(priceCents);
    if (priceEl) priceEl.textContent = priceStr;
    if (sizeLabel) sizeLabel.textContent = size;
    if (stickyName) stickyName.textContent = productName;
    if (stickyPrice) stickyPrice.textContent = priceStr;
    if (stickySize) stickySize.textContent = size;

    pdp.querySelectorAll('[data-size]').forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');
  }

  pdp.querySelectorAll('[data-size]').forEach(btn => {
    btn.addEventListener('click', () => updateSelection(btn));
    if (btn.classList.contains('selected')) updateSelection(btn);
  });

  // Add to cart handlers
  async function doAdd() {
    if (!selectedVariantId) return;
    try {
      await cartAdd(selectedVariantId, 1);
      await updateCartCount();
      showToast(`${productName} added to cart`);
      openCart();
    } catch (err) {
      console.error(err);
      showToast('Could not add to cart. Please try again.');
    }
  }
  pdp.querySelector('[data-add-to-cart]')?.addEventListener('click', doAdd);
  document.querySelector('[data-sticky-add]')?.addEventListener('click', doAdd);

  // Gallery
  const mainImg = pdp.querySelector('[data-pdp-main]');
  pdp.querySelectorAll('[data-pdp-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const src = thumb.getAttribute('data-pdp-thumb');
      if (mainImg && src) mainImg.style.backgroundImage = `url('${src}')`;
      pdp.querySelectorAll('[data-pdp-thumb]').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });
}

// ---------- Collection filters ----------
function initFilters() {
  const grid = document.querySelector('[data-product-grid]');
  if (!grid) return;
  const filters = { type: 'all', height: 'all' };

  document.querySelectorAll('[data-filter]').forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.getAttribute('data-filter');
      const val = btn.getAttribute('data-value');
      filters[key] = val;
      document.querySelectorAll(`[data-filter="${key}"]`).forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      applyFilters();
    });
  });

  function applyFilters() {
    grid.querySelectorAll('[data-product]').forEach(card => {
      const type = card.getAttribute('data-type');
      const height = card.getAttribute('data-height');
      const show =
        (filters.type === 'all' || filters.type === type) &&
        (filters.height === 'all' || filters.height === height);
      card.style.display = show ? '' : 'none';
    });
  }
}

// ---------- Header ----------
function initHeader() {
  document.querySelectorAll('[data-cart-open]').forEach(el => {
    el.addEventListener('click', e => { e.preventDefault(); openCart(); });
  });
  document.querySelectorAll('[data-cart-close]').forEach(el => {
    el.addEventListener('click', e => { e.preventDefault(); closeCart(); });
  });
  document.querySelector('.mobile-menu-btn')?.addEventListener('click', () => {
    document.querySelector('.header-nav')?.classList.toggle('open');
  });
}

// ---------- Boot ----------
document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  updateCartCount();
  initPdp();
  initFilters();
});
